import { useState, useEffect } from 'react';
import { API } from '@/App';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const STATUS_COLORS = {
  operational: '#1D9E75',
  degraded: '#C8A030',
  outage: '#E24B4A',
  partial_outage: '#C8A030',
  no_data: '#3D5A70',
};

const STATUS_LABELS = {
  operational: 'All Systems Operational',
  degraded: 'Minor Issues Detected',
  partial_outage: 'Partial Outage',
  outage: 'Major Outage',
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  function fetchStatus() {
    fetch(`${API}/public/status`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setData(d); setLastRefresh(new Date()); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-kova-bg flex items-center justify-center">
        <span className="font-mono text-kova-cyan text-sm animate-pulse">Loading status...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-kova-bg flex items-center justify-center">
        <span className="font-mono text-kova-red text-sm">Unable to fetch status</span>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[data.status] || STATUS_COLORS.operational;
  const statusLabel = STATUS_LABELS[data.status] || 'Unknown';

  return (
    <div className="min-h-screen bg-kova-bg text-white" data-testid="status-page">
      {/* Header */}
      <header className="border-b border-kova-cyan/12">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-['Syne'] font-extrabold text-lg tracking-wider">KO<span className="text-kova-cyan">VA</span></span>
            <span className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px] border-l border-kova-cyan/12 pl-3">System Status</span>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && <span className="text-[8px] font-mono text-kova-slate">Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button onClick={fetchStatus} className="text-[9px] font-mono text-kova-cyan hover:underline">Refresh</button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Overall Status Banner */}
        <div className="border border-kova-cyan/12 p-6" style={{ borderLeftWidth: 3, borderLeftColor: statusColor }} data-testid="status-banner">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: statusColor }} />
            <h1 className="font-['Syne'] font-bold text-xl" style={{ color: statusColor }}>{statusLabel}</h1>
          </div>
          <p className="text-[12px] font-mono text-kova-slate">
            {data.total_connections} integrations monitored &middot; {data.total_calls_24h.toLocaleString()} API calls in last 24h &middot; {data.uptime_pct}% uptime
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-4 gap-px bg-kova-cyan/12" data-testid="status-metrics">
          <div className="bg-kova-card p-4">
            <div className="text-2xl font-['Syne'] font-bold" style={{ color: data.uptime_pct >= 99 ? '#1D9E75' : data.uptime_pct >= 90 ? '#C8A030' : '#E24B4A' }}>
              {data.uptime_pct}%
            </div>
            <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px] mt-1">Uptime (24h)</div>
          </div>
          <div className="bg-kova-card p-4">
            <div className="text-2xl font-['Syne'] font-bold text-kova-cyan">
              {data.avg_latency_ms}<span className="text-sm text-kova-slate ml-0.5">ms</span>
            </div>
            <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px] mt-1">Avg Latency</div>
          </div>
          <div className="bg-kova-card p-4">
            <div className="text-2xl font-['Syne'] font-bold text-white">{data.total_connections}</div>
            <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px] mt-1">Integrations</div>
          </div>
          <div className="bg-kova-card p-4">
            <div className="text-2xl font-['Syne'] font-bold text-white">{data.total_calls_24h.toLocaleString()}</div>
            <div className="text-[8px] font-mono text-kova-slate uppercase tracking-[1.5px] mt-1">Calls (24h)</div>
          </div>
        </div>

        {/* Response Time Chart */}
        {data.hourly_trend.length > 1 && (
          <div className="border border-kova-cyan/12 bg-kova-card p-4" data-testid="latency-chart">
            <div className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px] mb-4">Response Time (12h)</div>
            <div style={{ height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.hourly_trend}>
                  <defs>
                    <linearGradient id="statusGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ABFA3" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ABFA3" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="hour" tick={{ fontSize: 8, fill: '#3D5A70', fontFamily: 'monospace' }} tickFormatter={h => h ? h.slice(11) + ':00' : ''} axisLine={{ stroke: 'rgba(10,191,163,0.12)' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 8, fill: '#3D5A70', fontFamily: 'monospace' }} axisLine={false} tickLine={false} unit="ms" width={45} />
                  <Tooltip
                    contentStyle={{ background: '#0C1220', border: '1px solid rgba(10,191,163,0.12)', fontFamily: 'monospace', fontSize: 10, color: '#7A9AB0' }}
                    formatter={v => [`${v}ms`, 'Avg Latency']}
                    labelFormatter={l => l ? l.slice(11) + ':00' : ''}
                  />
                  <Area type="monotone" dataKey="avg_latency" stroke="#0ABFA3" strokeWidth={1.5} fill="url(#statusGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Services List */}
        <div data-testid="services-list">
          <div className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px] mb-3">Integration Status</div>
          {data.services.length === 0 ? (
            <div className="border border-kova-cyan/12 bg-kova-card p-8 text-center">
              <p className="text-kova-slate font-mono text-sm">No integrations configured</p>
            </div>
          ) : (
            <div className="space-y-px">
              {data.services.map((svc, i) => {
                const sColor = STATUS_COLORS[svc.status] || STATUS_COLORS.operational;
                return (
                  <div key={i} className="flex items-center gap-4 px-4 py-3 border border-kova-cyan/12 bg-kova-card hover:bg-kova-bg transition-colors" data-testid={`service-row-${i}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sColor }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium">{svc.name}</span>
                    </div>
                    <div className="flex items-center gap-6 text-[11px] font-mono">
                      <span className="text-kova-slate">{svc.avg_latency_ms > 0 ? `${svc.avg_latency_ms}ms` : '\u2014'}</span>
                      <span className="text-kova-slate">{svc.calls_24h} calls</span>
                      <span style={{ color: sColor }}>{svc.uptime_pct}%</span>
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 border" style={{ color: sColor, borderColor: sColor + '40', background: sColor + '10' }}>
                        {svc.status === 'no_data' ? 'No Data' : svc.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Incidents */}
        {data.recent_incidents.length > 0 && (
          <div data-testid="recent-incidents">
            <div className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px] mb-3">Recent Incidents (Last Hour)</div>
            <div className="space-y-px">
              {data.recent_incidents.map((inc, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2 border border-kova-cyan/12 bg-kova-card text-[11px] font-mono">
                  <div className="w-1.5 h-1.5 rounded-full bg-kova-red flex-shrink-0" />
                  <span className="text-kova-red">{inc.response_status}</span>
                  <span className="text-kova-silv truncate flex-1">{inc.error_message || 'API error'}</span>
                  <span className="text-kova-slate flex-shrink-0">{new Date(inc.created_at).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="pt-6 border-t border-kova-cyan/12 flex items-center justify-between">
          <span className="text-[9px] font-mono text-kova-slate">KOVA Status &middot; Auto-refreshes every 30s</span>
          <span className="text-[9px] font-mono text-kova-slate">Powered by MERA Technology</span>
        </footer>
      </div>
    </div>
  );
}
